import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { supplierService, SupplierService } from "@/services/supplier";
import { streamingServiceApi } from "@/services/streaming-service";

interface AddSupplierServiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId: number;
  existingServices: SupplierService[];
}

export function AddSupplierServiceModal({
  open,
  onOpenChange,
  supplierId,
  existingServices,
}: AddSupplierServiceModalProps) {
  const queryClient = useQueryClient();
  const [serviceId, setServiceId] = useState<string>("");

  const { data: streamingServicesResponse } = useQuery({
    queryKey: ["streaming-services", "all"],
    queryFn: () => streamingServiceApi.getAll({ page: 1, limit: 100 }),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const streamingServices = streamingServicesResponse?.data || [];
  const existingServiceIds = new Set(existingServices.map((s) => s.serviceId));
  const availableServices = streamingServices.filter(
    (s) => !existingServiceIds.has(s.id),
  );

  const handleClose = () => {
    setServiceId("");
    onOpenChange(false);
  };

  const addMutation = useMutation({
    mutationFn: () =>
      supplierService.addService(supplierId, { serviceId: Number(serviceId) }),
    onSuccess: () => {
      toast.success("Servicio agregado al proveedor exitosamente");
      queryClient.invalidateQueries({
        queryKey: ["supplier", supplierId],
      });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = () => {
    if (!serviceId) {
      toast.error("Selecciona un servicio");
      return;
    }
    addMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar servicio al proveedor</DialogTitle>
          <DialogDescription>
            Selecciona un servicio de streaming que este proveedor pueda surtir.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Servicio</Label>
            <Select value={serviceId} onValueChange={setServiceId}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar servicio" />
              </SelectTrigger>
              <SelectContent>
                {availableServices.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No hay servicios disponibles para agregar
                  </div>
                ) : (
                  availableServices.map((service) => (
                    <SelectItem key={service.id} value={String(service.id)}>
                      {service.icon} {service.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={addMutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={addMutation.isPending || !serviceId}>
            {addMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Agregar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
